import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getRecommendations, getWorkById } from "@/utils/catalog";
import { categories } from "@/mocks/categories";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useFavorites } from "@/hooks/useFavorites";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EmptyState from "@/components/feature/EmptyState";
import SectionHeading from "@/components/feature/SectionHeading";
import WorkCard from "@/components/feature/WorkCard";
import CharacterCard from "@/pages/detail/components/CharacterCard";

const typeLabel: Record<string, string> = {
  movie: "电影",
  tv: "电视剧",
  web: "网络剧",
};

export default function DetailPage() {
  const { id } = useParams();
  const work = getWorkById(id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState("");

  usePageMeta({
    title: work ? `${work.title}剧情介绍与角色分析 - 妻子的朋友` : "影视详情 - 妻子的朋友",
    description: work ? `《${work.title}》${work.synopsis}` : undefined,
    keywords: work ? `${work.title},${work.genres.join(",")},剧情介绍,角色分析,妻子的朋友` : undefined,
  });

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 2000);
    return () => clearTimeout(t);
  }, [toast]);

  if (!work) {
    return (
      <div className="mx-auto w-full max-w-[1360px] px-4 py-16 md:px-6">
        <EmptyState
          title="未找到该影视作品"
          description="这部作品可能已下架或链接有误，返回首页继续浏览其他精彩内容。"
        />
        <div className="mt-6 flex justify-center">
          <Link
            to="/"
            className="flex h-11 items-center gap-2 rounded-md bg-primary-500 px-5 text-sm font-semibold text-background-50 cursor-pointer whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const category = categories.find((c) => c.id === work.type);
  const recommendations = getRecommendations(work, 6);
  const favorited = isFavorite(work.id);

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setToast("详情链接已复制到剪贴板");
    } catch {
      setToast("复制失败，请手动复制地址栏链接");
    }
  };

  return (
    <div className="w-full">
      <div className="relative w-full">
        <div className="absolute inset-0 h-[280px] overflow-hidden md:h-[360px]">
          <img
            src={work.backdrop}
            alt={`${work.title}剧照`}
            title={`${work.title}剧情介绍`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-primary-950/70 via-primary-950/60 to-background-50"></div>
        </div>

        <div className="relative mx-auto w-full max-w-[1360px] px-4 pb-4 pt-6 md:px-6 md:pt-8">
          <Breadcrumb
            tone="light"
            items={[
              { label: "分类", to: "/" },
              { label: category?.name ?? "影视", to: `/category/${work.type}` },
              { label: work.title },
            ]}
          />

          <div className="mt-6 flex flex-col gap-6 md:flex-row md:items-end">
            <div className="h-[300px] w-[200px] shrink-0 overflow-hidden rounded-lg border border-background-200 bg-background-200">
              <img
                src={work.poster}
                alt={`${work.title}封面`}
                title={`${work.title}影视封面`}
                className="h-full w-full object-cover object-top"
              />
            </div>

            <div className="min-w-0 flex-1 rounded-lg border border-background-200 bg-background-50/95 p-5 backdrop-blur md:p-6">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-md bg-primary-500 px-2 py-0.5 text-xs font-medium text-background-50">
                  {typeLabel[work.type]}
                </span>
                <span className="rounded-md bg-accent-100 px-2 py-0.5 text-xs font-medium text-accent-700">
                  {work.region}
                </span>
                <span className="rounded-md bg-secondary-100 px-2 py-0.5 text-xs font-medium text-secondary-900">
                  {work.year}
                </span>
              </div>

              <h1 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
                {work.title}
              </h1>
              <p className="mt-1 text-sm text-foreground-500">{work.titleEn} · {work.tagline}</p>

              <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
                <span className="flex items-center gap-1.5 text-accent-600 font-semibold">
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-star-fill"></i>
                  </span>
                  {work.rating.toFixed(1)} 分
                </span>
                <span className="text-foreground-600">
                  <strong className="font-medium text-foreground-800">导演：</strong>
                  {work.director}
                </span>
                <span className="text-foreground-600">
                  <strong className="font-medium text-foreground-800">热度：</strong>
                  {work.popularity.toLocaleString()}
                </span>
              </div>

              <p className="mt-3 text-sm leading-relaxed text-foreground-600 line-clamp-2">
                <strong className="font-medium text-foreground-800">主演：</strong>
                {work.cast.join("、")}
              </p>

              <div className="mt-5 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => toggleFavorite(work.id)}
                  className={`flex h-10 items-center gap-2 rounded-md px-4 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                    favorited
                      ? "bg-accent-500 text-background-50 hover:bg-accent-600"
                      : "bg-primary-500 text-background-50 hover:bg-primary-600"
                  }`}
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className={favorited ? "ri-heart-3-fill" : "ri-heart-3-line"}></i>
                  </span>
                  {favorited ? "已收藏" : "收藏本片"}
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="flex h-10 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 text-sm text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700 cursor-pointer whitespace-nowrap"
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-share-forward-line"></i>
                  </span>
                  分享链接
                </button>
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                {work.genres.map((g) => (
                  <Link
                    key={g}
                    to={`/search?q=${encodeURIComponent(g)}`}
                    className="rounded-full bg-secondary-100 px-2.5 py-1 text-xs text-secondary-900 transition-colors hover:bg-secondary-200 cursor-pointer"
                  >
                    {g}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1360px] px-4 py-10 md:px-6 md:py-12">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_300px]">
          <div className="flex flex-col gap-10">
            <section>
              <SectionHeading eyebrow="剧情介绍" title={`《${work.title}》剧情梗概`} />
              <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                {work.plot.map((para, i) => (
                  <p
                    key={i}
                    className="text-sm leading-7 text-foreground-700 [&:not(:first-child)]:mt-4"
                  >
                    {para}
                  </p>
                ))}
              </div>
            </section>

            <section>
              <SectionHeading
                eyebrow="角色分析"
                title="主要角色与人物解读"
                description="点击“展开角色详情”，查看每个人物的性格与在故事中的作用。"
              />
              <div className="flex flex-col gap-4">
                {work.characters.map((c, i) => (
                  <CharacterCard key={c.name} character={c} index={i} />
                ))}
              </div>
            </section>
          </div>

          <aside className="flex flex-col gap-5">
            {category && (
              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <h2 className="font-heading text-base font-bold text-foreground-950">所属分类</h2>
                <Link
                  to={`/category/${category.id}`}
                  className="mt-3 flex items-center gap-3 rounded-md border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300 cursor-pointer"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-500 text-background-50">
                    <i className={`${category.icon} text-xl`}></i>
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-foreground-900">
                      {category.name}
                    </span>
                    <span className="block text-xs text-foreground-500">浏览同类作品</span>
                  </span>
                  <span className="ml-auto flex h-5 w-5 items-center justify-center text-foreground-400">
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </Link>
              </div>
            )}

            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">作品信息</h2>
              <dl className="mt-3 flex flex-col gap-2.5 text-sm">
                <div className="flex justify-between gap-3">
                  <dt className="text-foreground-500">类型</dt>
                  <dd className="text-right text-foreground-800">{typeLabel[work.type]}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-foreground-500">地区</dt>
                  <dd className="text-right text-foreground-800">{work.region}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-foreground-500">上映年份</dt>
                  <dd className="text-right text-foreground-800">{work.year}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-foreground-500">评分</dt>
                  <dd className="text-right text-foreground-800">{work.rating.toFixed(1)}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-foreground-500">导演</dt>
                  <dd className="text-right text-foreground-800">{work.director}</dd>
                </div>
              </dl>
              <p className="mt-4 text-[11px] leading-relaxed text-foreground-400">
                本页评分、热度等数据为演示示例，仅用于展示页面效果。
              </p>
            </div>
          </aside>
        </div>

        <section className="mt-12">
          <SectionHeading
            eyebrow="观影推荐"
            title="相似题材推荐"
            description={`根据《${work.title}》的题材与风格，为你挑选可能同样合口味的作品。`}
          />
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {recommendations.map((w, i) => (
              <WorkCard key={w.id} work={w} index={i} />
            ))}
          </div>
        </section>
      </div>

      {toast && (
        <div className="fixed bottom-6 left-1/2 z-[70] -translate-x-1/2 rounded-lg bg-primary-900 px-5 py-3 text-sm text-background-50 animate-fade-up">
          {toast}
        </div>
      )}
    </div>
  );
}